import { gql } from './graphql.utils';
import { HttpHeaders } from '@angular/common/http';
import { ApolloLink, Observable as LinkObservable } from '@apollo/client/core';
import { AsyncJobStatusEnum } from '@ppl/graphql-space-api';
import { pollWhile } from '@ppl/utils/polling.utils';
import {
  merge,
  NEVER,
  Observable as RxObservable,
  of,
  throwError
} from 'rxjs';
import { delay, map, switchMap } from 'rxjs/operators';
import type { HttpResponse } from '@angular/common/http';
import type {
  FetchResult,
  Operation
} from '@apollo/client/core';
import type { AsyncJobStatus, Query } from '@ppl/graphql-space-api';
import type { HttpLink } from 'apollo-angular/http';
import type { LinkInfo } from './apollo.utils';


export class AsyncGraphQLHttpLink extends ApolloLink {
  private asyncJobHttpLink: ApolloLink;
  private headerName = 'Accept-Async-Request';

  constructor(
    private httpLink: HttpLink,
    private defaultLink: LinkInfo
  ) {
    super();
    const { headers, ...options } = this.defaultLink.linkOptions;
    this.asyncJobHttpLink = this.httpLink.create({
      ...options,
      headers: headers
        ? headers.set(this.headerName, 'True')
        : new HttpHeaders({ [this.headerName]: 'True' })
    });
  }

  request(
    operation: Operation,
  ): LinkObservable<FetchResult> | null {
    return new LinkObservable<FetchResult>(observer => {
      const subscription = this.tryUsingAsyncJob(operation).pipe(
        switchMap(result => {
          const context = operation.getContext() as (Required<AsyncGraphQLContext> & { response: HttpResponse<AsyncJobResponseBody> });

          if (context.response.status === 202) {
            const taskId = context.response.body.taskId;
            const asyncJob = new AsyncJob(
              taskId,
              (taskId: string) => this.getAsyncJobStatus(taskId)
            );

            const { pollPeriod, progressFn, pollTimeout } = context.asyncGraphQL;
            return asyncJob.start({
              pollPeriod,
              pollTimeout,
              progressFn
            });
          } else {
            return of(result);
          }
        })
      ).subscribe({
        next: result => {
          observer.next(result);
          observer.complete();
        },
        error: error => {
          observer.error(error);
        },
        complete: () => {
          observer.complete();
        }
      });

      return () => {
        if (!subscription.closed) {
          subscription.unsubscribe();
        }
      };
    });
  }

  private tryUsingAsyncJob(operation: Operation) {
    return new RxObservable<FetchResult>(observer => {
      return this.asyncJobHttpLink.request(operation).subscribe(observer);
    });
  }

  private getAsyncJobStatus(taskId: string) {
    return new RxObservable<FetchResult<Query>>(observer => {
      return ApolloLink.execute(this.defaultLink.link, {
        query: createAsyncJobStatusQuery(taskId)()
      }).subscribe(observer);
    }).pipe(
      map(({ data }) => {
        return data.collections.asyncJobStatus;
      })
    );
  }
}

class AsyncJob {
  private completeStatuses: AsyncJobStatusEnum[] = [
    AsyncJobStatusEnum.Completed,
    AsyncJobStatusEnum.Expired
  ];

  get jobId() {
    return this.id;
  }

  constructor(
    private id: string,
    private getStatus: AsyncJobGetStatusFn
  ) { }

  start(options: AsyncJobStartOptions = {}): RxObservable<FetchResult> {
    return merge(
      !options.pollTimeout || options.pollTimeout === ASYNC_JOB_STATUS_TIMEOUT_NOTSET
        ? NEVER
        : of(null).pipe(
          delay(options.pollTimeout),
          map(() => {
            return [{ event: 'timeout' } as AsyncJobEvent, true] as const;
          })
        ),
      pollWhile(
        this.getStatus(this.jobId),
        response => {
          return this.completeStatuses.includes(response.status);
        },
        { pollPeriod: options.pollPeriod || ASYNC_JOB_STATUS_DEFAULT_POLL_PERIOD }
      ).pipe(
        map(([response, isDone]) => {
          return [{ event: 'status', value: response } as AsyncJobEvent, isDone] as const;
        })
      )
    ).pipe(
      switchMap(([response, isDone]) => {
        if (isDone) {
          switch (response.event) {
            case 'timeout':
              return throwError(new AsyncJobTimeoutError());
            case 'status':
              switch (response.value.status) {
                case AsyncJobStatusEnum.Completed:
                  return of(JSON.parse(response.value.result));
                case AsyncJobStatusEnum.Expired:
                  return throwError(new AsyncJobExpiredError());
                default:
                  return NEVER;
              }
            default:
              return NEVER;
          }
        } else {
          if (response.event === 'status') {
            options.progressFn?.({
              jobId: this.jobId,
              total: response.value.total,
              processed: response.value.processed
            });
          }
          return NEVER;
        }
      })
    );
  }
}

const ASYNC_JOB_STATUS_DEFAULT_POLL_PERIOD = 1000;
export const ASYNC_JOB_STATUS_TIMEOUT_NOTSET = Symbol('notset');

type AsyncJobEvent = { event: 'timeout' } | { event: 'status', value: AsyncJobStatus };

interface AsyncJobProgress {
  jobId: string;
  total: number;
  processed: number;
}

type AsyncJobProgressFn = (progress: AsyncJobProgress) => void;
function createAsyncJobStatusQuery(taskId: string) {
  const id = taskId.replace(/[.-]/g, '_');
  return gql`
    query getAsyncJobStatus_${id} {
      collections {
    		asyncJobStatus(taskId: "${taskId}") {
    		  status
    		  total
    		  processed
    		  result
    		}
    	}
    }
  `;
}

type AsyncJobGetStatusFn = (id: string) => RxObservable<AsyncJobStatus>;
type AsyncJobTimeout = number | typeof ASYNC_JOB_STATUS_TIMEOUT_NOTSET;

interface AsyncJobStartOptions {
  pollPeriod?: number;
  pollTimeout?: AsyncJobTimeout;
  progressFn?: AsyncJobProgressFn;
}

interface AsyncJobResponseBody {
  taskId: string;
}

export class AsyncJobExpiredError extends Error {
  constructor() {
    super('Async job expired');
    this.name = 'AsyncJobExpiredError';
    Object.setPrototypeOf(this, AsyncJobExpiredError.prototype);
  }

  toJSON() {
    return JSON.stringify({ name: this.name, message: this.message });
  }
}

export class AsyncJobTimeoutError extends Error {
  constructor() {
    super('Async job timeout');
    this.name = 'AsyncJobTimeoutError';
    Object.setPrototypeOf(this, AsyncJobTimeoutError.prototype);
  }

  toJSON() {
    return JSON.stringify({ name: this.name, message: this.message });
  }
}

export interface AsyncGraphQLContext {
  // Instruct apollo client to try using async processing of graphql
  asyncGraphQL?: AsyncGraphQL;
}

export type AsyncGraphQLTimeout = AsyncJobTimeout;
export type AsyncGraphQLProgress = AsyncJobProgress;
export type AsyncGraphQLProgressFn = (progress: AsyncGraphQLProgress) => void;

export interface AsyncGraphQL {
  enabled: boolean;
  progressFn?: AsyncGraphQLProgressFn;
  pollPeriod?: number;
  pollTimeout?: AsyncGraphQLTimeout;
}

export function createProgressText(progress: { processed: number, total: number }): string {
  return `${progress.processed} / ${progress.total}`;
}
